
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Pagination,
  TextField,
  Paper,
  Button,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Avatar,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import Navbar from "./AdminNav";
import debounce from "lodash.debounce";
import api from "../api/api";
import { Link } from "react-router-dom";
import Footer from "../Footer";

const buildUserQueryConfig = (roleId, pageNo, pageSize, searchText) => ({
  params: {
    roleId,
    page: pageNo,
    pageSize,
    searchText: searchText || "",
    email: null,
    status: null,
  },
  paramsSerializer: {
    serialize: (params) =>
      Object.entries(params)
        .map(
          ([k, v]) =>
            `${encodeURIComponent(k)}=${v === null ? "null" : encodeURIComponent(v)}`
        )
        .join("&"),
  },
});

const ImageWithFallbackAndProfile = ({ user }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loaded) setFailed(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, [loaded]);

  const openTalentProfile = async () => {
    try {
      const res = await api.get("/api/User/GetUserName", {
        params: { userId: user.userId },
      });

      const username = res.data?.userName;
      if (!username) return;

      const base =
        process.env.REACT_APP_SITE_BASE_URL || window.location.origin;

      window.open(`${base}/${username}`, "_blank");
    } catch (err) {
      console.error("Error fetching username:", err);
    }
  };

  if (!user.talentImage || failed) {
    return (
      <Avatar
        variant="rounded"
        onClick={openTalentProfile}
        sx={{
          width: 40,
          height: 40,
          borderRadius: 6,
          backgroundColor: "#bdbdbd",
          cursor: "pointer",
        }}
      >
        <PersonIcon />
      </Avatar>
    );
  }

  return (
    <img
      src={user.talentImage}
      alt="thumb"
      style={{
        width: 40,
        height: 40,
        borderRadius: 6,
        objectFit: "cover",
        border: "1px solid #ccc",
        cursor: "pointer",
      }}
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
      onClick={openTalentProfile}
    />
  );
};

const UsersListPage = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [buttonLoading, setButtonLoading] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    user: null,
    action: "",
  });

  const [creditDialog, setCreditDialog] = useState(false);
  const [creditPoints, setCreditPoints] = useState("");
  const [creditLoading, setCreditLoading] = useState(false);

  const debouncedSearchRef = useRef(null);
  const latestSearch = useRef("");
  const clientId = localStorage.getItem("userId") || 0;

  const showToast = (msg, severity = "success") => {
    setToast({ open: true, message: msg, severity });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get("/api/Admin/GetAllRoles");
        setRoles(res.data?.data || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchRoles();
  }, []);

  const selectedRoleName =
    roles.find((r) => String(r.roleId) === String(selectedRole))?.roleName ||
    "";

  const fetchUsers = useCallback(
    async (roleId, pageNo = 1, search = "") => {
      latestSearch.current = search;
      setLoading(true);

      try {
        const res = await api.get(
          "/api/Admin/GetUserDetailsBasedOnRole",
          buildUserQueryConfig(roleId, pageNo, pageSize, search)
        );

        if (latestSearch.current !== search) return;

        setUsers(res.data?.data?.items || []);
        setTotalPages(res.data?.data?.totalPages || 1);
      } catch (err) {
        showToast("Failed to load users", "error");
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  const handleRoleChange = (e) => {
    const id = e.target.value;

    setSelectedRole(id);
    setPage(1);
    setSearchText("");

    if (id) {
      fetchUsers(id, 1, "");
    } else {
      setUsers([]);
    }
  };

  const handlePageChange = (e, val) => {
    setPage(val);
    fetchUsers(selectedRole, val, searchText);
  };

  const openConfirmDialog = (user, action) => {
    setConfirmDialog({ open: true, user, action });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, user: null, action: "" });
  };

  /*
   * Hide / Unhide / Block / Unblock
   *
   * Important:
   * These actions update visibility/blocking flags only.
   * They do NOT overwrite user.userStatus, which represents
   * the user's original account or membership status.
   */
  const handleVisibilityAction = async (user, actionType) => {
    if (!user || !actionType) return;

    setButtonLoading((prev) => ({
      ...prev,
      [user.userId]: true,
    }));

    const payload = {
      talentId: user.userId,
      clientId: Number(clientId) || 0,
    };

    if (actionType === "hide") payload.isHide = true;
    if (actionType === "unhide") payload.isHide = false;
    if (actionType === "block") payload.isBlock = true;
    if (actionType === "unblock") payload.isBlock = false;

    try {
      const res = await api.post(
        "/api/Admin/AddOrUpdateUserVisibility",
        payload
      );

      const success =
        res.data?.status?.toLowerCase() === "success" ||
        res.data?.result === true;

      if (success) {
        const messages = {
          hide: "User hidden",
          unhide: "User unhidden",
          block: "User blocked",
          unblock: "User unblocked",
        };

        showToast(messages[actionType] || "User updated");

        setUsers((prev) =>
          prev.map((u) => {
            if (u.userId !== user.userId) return u;

            return {
              ...u,

              // Change only the visibility flag.
              isHidden:
                actionType === "hide"
                  ? true
                  : actionType === "unhide"
                    ? false
                    : u.isHidden,

              // Change only the blocking flag.
              isBlocked:
                actionType === "block"
                  ? true
                  : actionType === "unblock"
                    ? false
                    : u.isBlocked,

              // Do not change u.userStatus here.
            };
          })
        );
      } else {
        showToast("Failed to update user", "error");
      }
    } catch (err) {
      showToast("Error updating user", "error");
    } finally {
      setButtonLoading((prev) => ({
        ...prev,
        [user.userId]: false,
      }));

      closeConfirmDialog();
    }
  };

  const handleOpenDeleteDialog = async (user) => {
    try {
      const res = await api.get(
        `/api/Admin/GetSubscriptionByEmail?email=${encodeURIComponent(user.email)}`
      );

      const subscription = res.data?.data;
      const isActive = subscription?.status?.toLowerCase() === "active";

      if (isActive) {
        showToast(
          "This user has an active subscription. Please cancel the subscription before deleting the account.",
          "warning"
        );
        return;
      }

      openConfirmDialog(user, "delete");
    } catch (err) {
      console.error("Error checking subscription:", err);

      showToast(
        "Unable to verify the user's subscription. Account was not deleted.",
        "error"
      );
    }
  };

  const handleDeleteUser = async (user) => {
    if (!user) return;

    setButtonLoading((prev) => ({
      ...prev,
      [user.userId]: true,
    }));

    try {
      const res = await api.post("/api/Admin/DeleteUser", {
        email: user.email,
      });

      const success =
        res.data?.status?.toLowerCase() === "ok" ||
        res.data?.result === true;

      if (success) {
        showToast("User deleted");

        setUsers((prev) =>
          prev.map((u) =>
            u.userId === user.userId
              ? {
                  ...u,
                  userStatus: "Deleted",
                  isHidden: false,
                  isBlocked: false,
                }
              : u
          )
        );
      } else {
        showToast("Failed to delete user", "error");
      }
    } catch (err) {
      showToast("Error deleting user", "error");
    } finally {
      setButtonLoading((prev) => ({
        ...prev,
        [user.userId]: false,
      }));

      closeConfirmDialog();
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value.trimStart();
    setSearchText(val);

    if (debouncedSearchRef.current) {
      debouncedSearchRef.current(val, selectedRole);
    }
  };

  useEffect(() => {
    debouncedSearchRef.current = debounce((val, role) => {
      setPage(1);

      if (role) {
        fetchUsers(role, 1, val);
      }
    }, 350);

    return () => {
      debouncedSearchRef.current?.cancel();
    };
  }, [fetchUsers]);

  const handleGrantCredits = async () => {
    const value = Number(creditPoints);

    if (!creditPoints || Number.isNaN(value) || value <= 0) {
      showToast("Enter a valid number of credits", "warning");
      return;
    }

    setCreditLoading(true);

    try {
      const res = await api.post(
        "/api/Admin/GrantCreditsToInactiveTalents",
        { creditPoints: value }
      );

      const success =
        res.data?.status?.toLowerCase() === "success" ||
        res.data?.result === true;

      if (success) {
        showToast("Credits granted successfully!", "success");
        setCreditDialog(false);
        setCreditPoints("");

        if (selectedRole) {
          fetchUsers(selectedRole, page, searchText);
        }
      } else {
        showToast("Failed to grant credits", "error");
      }
    } catch (err) {
      showToast("Error granting credits", "error");
    } finally {
      setCreditLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: "1400px", mx: "auto", p: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Users List</Typography>

          <Stack direction="row" spacing={2}>
            {selectedRoleName === "USER" && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setCreditDialog(true)}
              >
                Add Credits
              </Button>
            )}

            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/admin/add-user"
            >
              Add User
            </Button>
          </Stack>
        </Box>

        {/* ROLE SELECT */}
        <Select
          fullWidth
          value={selectedRole}
          onChange={handleRoleChange}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span style={{ color: "#888" }}>
                  -- Select Role --
                </span>
              );
            }

            const role = roles.find(
              (r) => String(r.roleId) === String(selected)
            );

            return role?.roleName || "";
          }}
          sx={{ mb: 2 }}
        >
          {roles.map((r) => (
            <MenuItem key={r.roleId} value={r.roleId}>
              {r.roleName}
            </MenuItem>
          ))}
        </Select>

        {/* SEARCH */}
        {selectedRole && (
          <TextField
            label="Search by any field"
            fullWidth
            value={searchText}
            onChange={handleSearchChange}
            sx={{ mb: 3 }}
          />
        )}

        {/* TABLE */}
        {selectedRole && (
          <Paper elevation={3} sx={{ overflowX: "auto", width: "100%" }}>
            <Box sx={{ minWidth: 1000 }}>
              <Table
                sx={{
                  tableLayout: "auto",
                  "& .MuiTableCell-root": {
                    padding: "6px 8px",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 70 }}>User ID</TableCell>
                    <TableCell sx={{ width: 60 }}>Photo</TableCell>
                    <TableCell sx={{ width: 150 }}>Full Name</TableCell>
                    <TableCell sx={{ width: 200 }}>Email</TableCell>
                    <TableCell sx={{ width: 120 }}>Login</TableCell>

                    {selectedRoleName === "USER" && (
                      <TableCell sx={{ width: 80 }}>Credits</TableCell>
                    )}

                    <TableCell sx={{ width: 90 }}>Status</TableCell>
                    <TableCell sx={{ width: 220 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={selectedRoleName === "USER" ? 8 : 7}
                        align="center"
                      >
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={selectedRoleName === "USER" ? 8 : 7}
                        align="center"
                      >
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => {
                      const status = (
                        user.userStatus || "Active"
                      ).toLowerCase();

                      const isDeleted = status === "deleted";

                      /*
                       * Prefer separate flags when available.
                       * Fall back to legacy status values for
                       * records returned by the existing API.
                       */
                      const isHidden =
                        typeof user.isHidden === "boolean"
                          ? user.isHidden
                          : ["hidden", "hided", "hide"].includes(status);

                      const isBlocked =
                        typeof user.isBlocked === "boolean"
                          ? user.isBlocked
                          : status === "blocked";

                      /*
                       * Membership/account status is independent
                       * of visibility and blocking status.
                       */
                      const displayStatus = isDeleted
                        ? "Deleted"
                        : isHidden
                          ? "Hidden"
                          : isBlocked
                            ? "Blocked"
                            : status === "inactive" &&
                                Number(user.userCredits) > 0
                              ? "Free Trial"
                              : user.userStatus || "Active";

                      const btnLoading = buttonLoading[user.userId];

                      return (
                        <TableRow key={user.userId}>
                          {/* USER ID */}
                          <TableCell
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={String(user.userId)}
                          >
                            {user.userId}
                          </TableCell>

                          {/* PHOTO */}
                          <TableCell>
                            {(user.roleName || selectedRoleName).toUpperCase() ===
                            "USER" ? (
                              <a
                                href={`${
                                  process.env.REACT_FRONTEND_APP_SITE_BASE_URL ||
                                  window.location.origin
                                }/talent/${user.userId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                <ImageWithFallbackAndProfile user={user} />
                              </a>
                            ) : (
                              <Link
                                to={`/admin/user/${encodeURIComponent(
                                  user.email
                                )}?role=${encodeURIComponent(
                                  user.roleName || selectedRoleName
                                )}`}
                                target="_blank"
                                style={{ textDecoration: "none" }}
                              >
                                <ImageWithFallbackAndProfile user={user} />
                              </Link>
                            )}
                          </TableCell>

                          {/* FULL NAME */}
                          <TableCell
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={`${user.firstName} ${user.lastName}`}
                          >
                            <Link
                              to={`/admin/user/${encodeURIComponent(
                                user.email
                              )}?role=${encodeURIComponent(
                                user.roleName || selectedRoleName
                              )}`}
                              target="_blank"
                              style={{
                                color: "#1976d2",
                                textDecoration: "none",
                                fontWeight: 500,
                              }}
                            >
                              {user.firstName} {user.lastName}
                            </Link>
                          </TableCell>

                          {/* EMAIL */}
                          <TableCell
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={user.email}
                          >
                            {user.email}
                          </TableCell>

                          {/* LOGIN */}
                          <TableCell
                            sx={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {user.loginName} / {user.password}
                          </TableCell>

                          {/* CREDITS */}
                          {selectedRoleName === "USER" && (
                            <TableCell>{user.userCredits}</TableCell>
                          )}

                          {/* STATUS */}
                          <TableCell>
                            <Typography
                              color={
                                displayStatus === "Free Trial"
                                  ? "info.main"
                                  : displayStatus === "Inactive"
                                    ? "error.main"
                                    : isDeleted
                                      ? "text.disabled"
                                      : isBlocked
                                        ? "error.main"
                                        : isHidden
                                          ? "text.secondary"
                                          : "success.main"
                              }
                              sx={{ fontWeight: 500 }}
                            >
                              {displayStatus}
                            </Typography>
                          </TableCell>

                          {/* ACTIONS */}
                          <TableCell sx={{ overflow: "visible" }}>
                            <Stack
                              direction="row"
                              gap={1}
                              flexWrap="nowrap"
                            >
                              {/* HIDE / UNHIDE */}
                              {!isHidden ? (
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  disabled={
                                    btnLoading || isBlocked || isDeleted
                                  }
                                  onClick={() =>
                                    openConfirmDialog(user, "hide")
                                  }
                                >
                                  {btnLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    "Hide"
                                  )}
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  disabled={btnLoading || isDeleted}
                                  onClick={() =>
                                    handleVisibilityAction(user, "unhide")
                                  }
                                >
                                  {btnLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    "Unhide"
                                  )}
                                </Button>
                              )}

                              {/* BLOCK / UNBLOCK */}
                              {!isBlocked ? (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  disabled={btnLoading || isDeleted}
                                  onClick={() =>
                                    openConfirmDialog(user, "block")
                                  }
                                >
                                  {btnLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    "Block"
                                  )}
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="info"
                                  size="small"
                                  disabled={btnLoading || isDeleted}
                                  onClick={() =>
                                    handleVisibilityAction(user, "unblock")
                                  }
                                >
                                  {btnLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    "Unblock"
                                  )}
                                </Button>
                              )}

                              {/* DELETE */}
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                disabled={btnLoading || isDeleted}
                                onClick={() =>
                                  handleOpenDeleteDialog(user)
                                }
                              >
                                {btnLoading ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  "Delete"
                                )}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        )}

        {/* PAGINATION */}
        {selectedRole && (
          <Box mt={3} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
            />
          </Box>
        )}
      </Box>

      {/* TOAST MESSAGE */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* CONFIRMATION DIALOG */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
      >
        <DialogTitle>
          {confirmDialog.action === "delete"
            ? "Confirm Delete"
            : confirmDialog.action === "block"
              ? "Confirm Block"
              : "Confirm Hide"}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {confirmDialog.action === "delete"
              ? "delete"
              : confirmDialog.action === "block"
                ? "block"
                : "hide"}{" "}
            {confirmDialog.user?.firstName}?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeConfirmDialog}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color={
              confirmDialog.action === "delete" ||
              confirmDialog.action === "block"
                ? "error"
                : "warning"
            }
            disabled={
              !confirmDialog.user ||
              Boolean(buttonLoading[confirmDialog.user?.userId])
            }
            onClick={() => {
              if (confirmDialog.action === "delete") {
                handleDeleteUser(confirmDialog.user);
              } else {
                handleVisibilityAction(
                  confirmDialog.user,
                  confirmDialog.action
                );
              }
            }}
          >
            {confirmDialog.action === "delete"
              ? "Delete"
              : confirmDialog.action === "block"
                ? "Block"
                : "Hide"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD CREDITS DIALOG */}
      <Dialog
        open={creditDialog}
        onClose={() => setCreditDialog(false)}
      >
        <DialogTitle>
          Grant Credits to Inactive Users
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the number of credits you want to grant to all inactive USER
            accounts.
          </DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            label="Credit Points"
            type="number"
            fullWidth
            value={creditPoints}
            onChange={(e) => {
              let val = e.target.value;

              if (val < 0) val = "";
              if (val > 100000) val = "100000";

              setCreditPoints(val);
            }}
            inputProps={{ min: 1, max: 100000 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCreditDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleGrantCredits}
            disabled={creditLoading}
          >
            {creditLoading ? (
              <CircularProgress size={18} />
            ) : (
              "Grant Credits"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </>
  );
};

export default UsersListPage;
